import { MinzeElement } from 'minze';
import { Swiper } from 'swiper';
import { Locale } from '../../modules/locale';
import { Navigation } from 'swiper/modules';

// API Response Interface
interface ApiForumPost {
  discussionID: number;
  name: string;
  body: string;
  dateInserted: string;
  insertUser: {
    name: string;
    photoUrl: string;
    pronounLabel?: {
      fr: string;
      en: string;
    } | null
  };
  category: {
    name: string;
  };
  url: string;
}

// Internal Component Interface
interface ForumPost {
  id: string;
  username: string;
  pronoun: string | null;
  avatar: string;
  timestamp: string;
  title: string;
  content: string;
  categories: string[];
  url: string;
}

export class TjForumPostSlider extends MinzeElement {
  private swiper: Swiper | null = null;
  private posts: ForumPost[] = [];
  private isLoading: boolean = true;
  private hasError: boolean = false;

  async onReady() {
    await this.fetchForumPosts();
  }

  onDestroy() {
    if (this.swiper) {
      this.swiper.destroy();
    }
  }

  private async fetchForumPosts(): Promise<void> {
    try {
      this.isLoading = true;
      this.hasError = false;
      
      // Create abort controller for 10-second timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(
        'https://storage.googleapis.com/tj-prod-public-bucket/forum-discussions.json',
        { 
          signal: controller.signal
        }
      );
      
      // Clear timeout if request completes
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const apiData: ApiForumPost[] = await response.json();
      this.posts = this.transformApiData(apiData);
      this.isLoading = false;

      // Re-render and initialize Swiper
      this.rerender();
      requestAnimationFrame(() => {
        this.initializeSwiper();
      });
    } catch (error: any) {
      // Handle both network errors and timeout
      if (error.name === 'AbortError') {
        console.warn('Forum posts fetch timed out after 10 seconds');
      } else {
        console.warn('Failed to fetch forum posts:', error);
      }
      
      this.hasError = true;
      this.isLoading = false;
      this.rerender(); // This will render empty string, hiding component
    }
  }

  private transformApiData(apiPosts: ApiForumPost[]): ForumPost[] {
    return apiPosts.map(post => ({
      id: post.discussionID.toString(),
      username: post.insertUser.name,
      pronoun: post.insertUser?.pronounLabel?.[Locale.language as 'fr' | 'en'] || null,
      avatar: post.insertUser.photoUrl,
      timestamp: this.formatRelativeTime(post.dateInserted),
      title: post.name,
      content: this.sanitizeHTML(post.body),
      categories: [post.category.name.toUpperCase()],
      url: post.url
    }));
  }

  private formatRelativeTime(dateString: string): string {
    const postDate = new Date(dateString);
    const now = Date.now();
    const diffInMs = now - postDate.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    
    // Less than 24 hours: show in hours
    if (diffInHours < 24) {
      if (diffInHours < 1) {
        return '1hr';
      }
      const roundedHours = Math.round(diffInHours);
      return `${roundedHours}hr`;
    }
    
    // 24+ hours: show in days with "j" suffix
    const diffInDays = diffInHours / 24;
    const roundedDays = Math.round(diffInDays);
    return `${roundedDays}j`;
  }

  private sanitizeHTML(htmlContent: string): string {
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Extract text content only
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    // Trim and limit length for preview
    return textContent.trim();
  }

  private initializeSwiper() {
    const swiperContainer = this.select('.swiper');
    if (swiperContainer) {
      this.swiper = new Swiper(swiperContainer, {
        modules: [Navigation],
        speed: 500,
        navigation: {
          nextEl: this.select('.swiper-button-next'),
          prevEl: this.select('.swiper-button-prev'),
          disabledClass: 'swiper-button-disabled'
        },
        slidesPerView: 1.2,
        spaceBetween: 16,
        breakpoints: {
          991: {
            slidesPerView: 2,
            spaceBetween: 24,
          },
        },
      });
    }
  }

  private renderSkeletonCard(): string {
    return /*html*/`
      <div class="swiper-slide">
        <div class="forum-post-card skeleton-card">
          <div class="post-header">
            <div class="skeleton-avatar"></div>
            <div class="user-info">
              <div class="skeleton-text skeleton-username"></div>
              <div class="skeleton-text skeleton-details"></div>
            </div>
            <div class="skeleton-text skeleton-timestamp"></div>
          </div>
          <div class="skeleton-text skeleton-title"></div>
          <div class="skeleton-text skeleton-content-line"></div>
          <div class="skeleton-text skeleton-content-line"></div>
          <div class="skeleton-text skeleton-content-line short"></div>
          <div class="skeleton-categories">
            <div class="skeleton-tag"></div>
          </div>
        </div>
      </div>
    `;
  }

  private renderPost(post: ForumPost): string {
    return /*html*/`
      <a href="${post.url}" class="swiper-slide forum-post-card" target="_blank" rel="noopener noreferrer">
          <div class="post-header">
            <img src="${post.avatar}" alt="${post.username}" class="user-avatar">
            <div class="user-info">
              <span class="username">${post.username}</span>
              <span class="pronoun">${post.pronoun}</span>
            </div>
            <span class="timestamp">${post.timestamp}</span>
          </div>
          <div class="post-title text-clamp-1">
            ${post.title}
          </div>
          <div class="post-content text-clamp-3">
            ${post.content}
          </div>
          <div class="post-categories">
            ${post.categories.map(category => `<span class="category-tag text-clamp-1">${category}</span>`).join('')}
          </div>
        </a>
    `;
  }

  html = () => {
    // Don't render anything if there's an error
    if (this.hasError) {
      return '';
    }
    
    // Show skeleton while loading
    if (this.isLoading) {
      return /*html*/`
        <div class="swiper-container">
          <div class="swiper custom-swiper">
            <div class="swiper-wrapper custom-swiper-wrapper">
              ${Array(4).fill(0).map(() => this.renderSkeletonCard()).join('')}
            </div>
          </div>
          <div>
            <div class="swiper-button-prev"><tj-prev-slide-icon></tj-prev-slide-icon></div>
            <div class="swiper-button-next"><tj-next-slide-icon></tj-next-slide-icon></div>
          </div>
        </div>
      `;
    }
    
    // Show real content
    return /*html*/`
      <div class="swiper-container">
        <div class="swiper custom-swiper">
          <div class="swiper-wrapper custom-swiper-wrapper">
            ${this.posts.map(post => this.renderPost(post)).join('')}
          </div>
        </div>
        <div>
          <div class="swiper-button-prev"><tj-prev-slide-icon></tj-prev-slide-icon></div>
          <div class="swiper-button-next"><tj-next-slide-icon></tj-next-slide-icon></div>
        </div>
      </div>
    `;
  };

  css = () => `
    /* Essential Swiper CSS - included directly */
    .swiper {
      margin-left: auto;
      margin-right: auto;
      position: relative;
      overflow: hidden;
      list-style: none;
      padding: 0;
      z-index: 1;
    }

    .swiper-wrapper {
      position: relative;
      width: 100%;
      height: 100%;
      z-index: 1;
      display: flex;
      transition-property: transform;
      box-sizing: content-box;
    }

    .swiper-slide {
      flex-shrink: 0;
      width: 100%;
      position: relative;
      transition-property: transform;
    }

    /* Component Styles */
    .swiper-button-disabled {
      opacity: 0.5;
      pointer-events: none;
      cursor: default;
    }

    .swiper-container {
      margin-bottom: 4rem;
      position: relative;
    }

    .swiper-button-next, .swiper-button-prev {
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 18px;
      width: 46px;
      height: 46px;
      bottom: -64px;
      z-index: 10;
      background: #133762;
      cursor: pointer;
      transition: all ease 0.2s;
    }

    .swiper-button-next:hover, .swiper-button-prev:hover {
      scale: 1.05;
    }

    .swiper-button-prev {
      right: calc(48px + 8px);
    }

    .swiper-button-next {
      right: 0px;
    }

    .swiper.custom-swiper {
      overflow: hidden!important;
      padding: 1rem 0!important;
    }

    .swiper-wrapper.custom-swiper-wrapper {
      overflow: visible;
    }

    .forum-post-card {
      display: flex;
      flex-direction: column;
      color: #123661;
      cursor: pointer;
      text-decoration: none;
      font-family: inherit;
      background: white;
      border-radius: 12px;
      border: 1px solid #ddd;
      padding: 20px;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }

    .forum-post-card:hover {
      transform: translateY(-2px);
    }

    .post-header {
      display: flex;
      align-items: flex-start;
      margin-bottom: 12px;
    }

    .user-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      margin-right: 12px;
      object-fit: cover;
    }

    .user-info {
      display: flex;
      flex-direction: column;
      flex: 1;
    }

    .username {
      font-weight: 600;
      font-size: 14px;
      color: #2c3e50;
      margin-bottom: 2px;
    }

    .pronoun {
      font-size: 12px;
      color: #4f4f4f;
      font-weight: 400;
    }

    .timestamp {
      font-size: 12px;
      color: #7f8c8d;
      margin-left: auto;
    }

    .post-title {
      font-weight: 700;
      font-size: 16px;
      color: #2c3e50;
      margin-bottom: 12px;
      line-height: 1.3;
    }

    .text-clamp-1 {
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
    }

    .text-clamp-3 {
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
    }

    .post-content {
      flex: 1;
      font-size: 14px;
      line-height: 1.5;
      color: #5a6c7d;
      margin-bottom: 16px;
    }

    .post-categories {
      margin-top: auto;
      display: flex;
    }

    .category-tag {
      background: #e6f7ff;
      color: #0192e1;
      padding: 0 12px;
      border-radius: 16px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      line-height: 25px;
    }

    /* Skeleton Loading Styles */
    .skeleton-card {
      pointer-events: none;
    }

    .skeleton-text {
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: skeleton-loading 1.5s infinite;
      border-radius: 4px;
      height: 12px;
      margin-bottom: 8px;
    }

    .skeleton-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: skeleton-loading 1.5s infinite;
      margin-right: 12px;
    }

    .skeleton-username {
      width: 120px;
      height: 14px;
      margin-bottom: 4px;
    }

    .skeleton-details {
      width: 80px;
      height: 10px;
    }

    .skeleton-timestamp {
      width: 30px;
      height: 10px;
      margin-left: auto;
    }

    .skeleton-title {
      width: 90%;
      height: 16px;
      margin-bottom: 12px;
    }

    .skeleton-content-line {
      width: 100%;
      height: 12px;
      margin-bottom: 6px;
    }

    .skeleton-content-line.short {
      width: 60%;
    }

    .skeleton-categories {
      display: flex;
      gap: 8px;
      margin-top: auto;
    }

    .skeleton-tag {
      width: 80px;
      height: 24px;
      border-radius: 16px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: skeleton-loading 1.5s infinite;
    }

    @media only screen and (max-width: 990px) {
      .swiper-container {
        margin-bottom: 0;
      }
      .swiper.custom-swiper {
        overflow: visible!important;
      }
      .swiper-button-next, .swiper-button-prev {
        display: none;
      }
    }

    @keyframes skeleton-loading {
      0% {
        background-position: 200% 0;
      }
      100% {
        background-position: -200% 0;
      }
    }
  `;
}
